/**
 * CouponRedemptionList 列表组件
 *
 * 横向滚动展示卡片列表，包含"购买更多"按钮
 *
 * Requirements:
 * - 7.1: 点击单张券跳转到券详情页
 * - 7.2: 点击"购买更多"按钮跳转到购买会员产品页
 */

import { goToNativePage, goToPageV2 } from "@/utils/common";
import React, { useCallback } from "react";
import {
  CardItemType,
  DisplayItem,
  UnifiedCardItem,
} from "../../types/coupon-redemption";
import RedemptionCard from "../redemption-card";
import "./index.less";

export interface CouponRedemptionListProps {
  /** 展示项列表 */
  items: DisplayItem[];
  /** 点击购买更多回调 */
  onBuyMoreClick?: () => void;
  /** 点击卡片回调 */
  onCardClick?: (item: UnifiedCardItem, displayItem: DisplayItem) => void;
}

/**
 * 促核销列表组件
 *
 * @param props 组件属性
 */
const CouponRedemptionList: React.FC<CouponRedemptionListProps> = ({
  items,
  onBuyMoreClick,
  onCardClick,
}) => {
  // 跳转到购买更多页面
  const handleBuyMoreClick = useCallback(() => {
    onBuyMoreClick?.();
    // 跳转会员购买页
    goToPageV2({
      hash: "/vip-goods",
      pathname: window.location.pathname,
    });
  }, [onBuyMoreClick]);

  // 处理卡片点击
  const handleCardClick = useCallback(
    (item: UnifiedCardItem, displayItem: DisplayItem) => {
      onCardClick?.(item, displayItem);

      if (item.itemType === CardItemType.StoredCard) {
        // 储值卡 → 卡包列表储值卡tab（多端适配）
        goToNativePage({
          app: "xiaopeng://charging/card?type=2",
          mini: `/charging/pages/voucher-list/index?tabIndex=2`,
          browser: `https://app.xiaopeng.com/activity/openurl?url=${encodeURIComponent("xiaopeng://charging/card?type=2")}`,
        });
        return;
      }

      // 优惠券
      if (displayItem.isAggregated) {
        // 聚合券 → 跳转到卡包列表的券tab
        goToNativePage({
          mini: `/charging/pages/voucher-list/index?tabIndex=1`,
          app: "xiaopeng://charging/card?type=1",
          browser: `https://app.xiaopeng.com/activity/openurl?url=${encodeURIComponent("xiaopeng://charging/card?type=1")}`,
        });
      } else {
        // 单张券 → 券详情H5页面
        const { origin } = window.location;
        goToPageV2({
          url: `${origin}/apps/charge-web/coupon-detail.html?couponId=${item.couponId}`,
        });
      }
    },
    [onCardClick],
  );

  // 获取展示项的代表数据
  const getItemData = (displayItem: DisplayItem): UnifiedCardItem => {
    return displayItem.isAggregated
      ? displayItem.aggregatedData!.representative
      : displayItem.data!;
  };

  // 获取聚合数量
  const getAggregateCount = (displayItem: DisplayItem): number | undefined => {
    return displayItem.isAggregated
      ? displayItem.aggregatedData?.count
      : undefined;
  };

  return (
    <div
      className="coupon-redemption-list"
      onScroll={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div className="list-scroll-container">
        {items.map((displayItem) => {
          const itemData = getItemData(displayItem);
          const aggregateCount = getAggregateCount(displayItem);

          return (
            <RedemptionCard
              key={displayItem.id}
              item={itemData}
              showNewBadge={displayItem.showNewBadge}
              showCountdown={displayItem.showCountdown}
              aggregateCount={aggregateCount}
              onClick={() => handleCardClick(itemData, displayItem)}
            />
          );
        })}

        {/* 购买更多按钮 */}
        <div className="buy-more-btn" onClick={handleBuyMoreClick}>
          <span>购买</span>
          <span>更多</span>
        </div>
      </div>
    </div>
  );
};

export default CouponRedemptionList;
